// Central handling for an expired or invalid session.
//
// A 401 caused by an expired JWT is deterministic: retrying it never succeeds.
// Callers that poll (unread counts, message refresh) otherwise retry forever —
// one client produced 2,705 consecutive 401s on /api/messagelist over 11 hours
// because the failure was only ever logged to the console.
//
// The first 401 tears the session down and announces it once, so pollers can
// stop and the UI can ask the user to sign in again.

import { disconnectSocket } from "./socketClient";
import { stopMessageNotifications } from "./messageNotifier";

export const SESSION_EXPIRED_EVENT = "session-expired";

// Keys written at login. Mirrors the list cleared by ProfileDropdown.logout().
const SESSION_KEYS = [
  "auth-token",
  "userid",
  "username",
  "email",
  "userlogid",
  "logintime",
];

// Not every 401 means the session died. The API also returns 401 for ordinary
// validation failures — a wrong old password on the change-password form, a
// failed login, an expired email link — and signing the user out for those
// would be a worse bug than the one this module fixes.
//
// So this is an allowlist of the shapes the auth layer actually produces
// (authMiddleware.verifyToken/requireAdmin and the "Unauthorized" guards).
// Anything unrecognised is left alone: the caller just sees a normal rejection.
const AUTH_FAILURE_PATTERNS = [
  /access denied/i,
  /invalid (user )?token/i,
  /invalid or expired token/i,
  /^unauthorized/i,
];

export function isAuthFailure(response) {
  if (!response || response.status !== 401) return false;

  const data = response.data;
  if (typeof data === "string") {
    return AUTH_FAILURE_PATTERNS.some((re) => re.test(data));
  }
  if (!data || typeof data !== "object") return false;

  // authMiddleware marks token rejections explicitly.
  if (data.valid === false) return true;

  const text = data.error || data.message || "";
  return AUTH_FAILURE_PATTERNS.some((re) => re.test(text));
}

let alreadyHandled = false;

/**
 * Clear the dead session and notify the app. Safe to call from several places
 * at once: parallel requests failing together produce a single teardown.
 */
export function handleSessionExpired() {
  if (alreadyHandled) return;
  alreadyHandled = true;

  // Clear before dispatching so listeners observe a clean, signed-out state.
  SESSION_KEYS.forEach((key) => localStorage.removeItem(key));

  // The server authenticated the handshake back when the token was still good
  // and keeps the socket open after it expires, so presence would show this
  // user online indefinitely after they were signed out.
  stopMessageNotifications();
  disconnectSocket();

  document.cookie =
    "jwt-token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";

  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
}

/**
 * Re-arm the guard after a successful sign-in, so a later expiry in the same
 * page session is handled again rather than silently ignored.
 */
export function resetSessionExpiry() {
  alreadyHandled = false;
}
